import Clear from '@mui/icons-material/Clear';
import ControlPointOutlined from '@mui/icons-material/ControlPointOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BoxOwnProps, styled } from '@mui/system';
import { JobState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React, { useCallback, useState } from 'react';

import { Chip } from '~components/Chip/Chip';
import LoadCustomRateIndicator from '~components/Job/LoadCustomRateIndicator';
import { Status as LoadStatus } from '~components/Job/LoadStatus';
import UomMismatchIndicator from '~components/Job/UomMismatchIndicator';
import { FeatureFlags } from '~constants/featureFlags';
import useAiReviewDismissal from '~hooks/useAiReview/useAiReviewDismissal';
import { trackLoadsApproved } from '~hooks/useAnalytics/trackApprovals';
import { useJob } from '~hooks/useJob/useJob';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import DuplicateTicketIndicator from '~pages/Approvals/ApprovalsComponents/DuplicateTicketIndicator';
import LoadReviewIndicator from '~pages/Approvals/ApprovalsComponents/LoadReviewIndicator';
import { TicketExportIndicator } from '~pages/Approvals/ApprovalsComponents/TicketExport';
import ApprovalActionsMenu from '~pages/Approvals/DriverDay/components/ApprovalActionsMenu';
import LoadStatusTransitioner from '~pages/Dispatch/components/LoadStatusTransitioner';
import TicketSourceBadge from '~pages/Dispatch/components/tickets-table/TicketSourceBadge';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import { useStores } from '~store';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import { dateFormat, formatDuration, minutesToHoursMins } from '~utils/dateTime';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { ATTACHMENT_IMAGE_CROSS_ORIGIN } from '~utils/image/attachmentImageCrossOrigin';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { flaggedLoadRowSx } from '~utils/loads/load-utils';
import { getExpectedJobUom, loadHasUomMismatch } from '~utils/uomMismatch';
import { numberToIntegerOrDecimal } from '~utils/utilFunctions';

import {
  LoadingSpinner,
  OverflowAwareText,
} from '../Order/ordersDispatchStyledComponents';

const IMAGE_SIZE_IN_PX = 50;

interface Props {
  companyId: string;
  cycleTime: number;
  index: number;
  isDeletable: boolean;
  isLast?: boolean;
  load: Load;
  onClick: () => void;
  onRemoveLoad: (id: string) => void | Promise<void>;
  reload: () => void;
  sx?: SxProps<Theme>;
  ticket: Nullable<Ticket>;
  isEditing?: boolean;
  readonly?: boolean;
  jobState?: JobState;
  updateLoad: (load: Load) => void;
  disableApproval?: boolean;
  isOrderEditable?: boolean;
  workLockReason?: Nullable<string>;
  /** Job's equipment capacity UOM (takes priority over loadUom) */
  equipmentCapacityUom?: Nullable<ItemNameAndId>;
  /** Job's load UOM (fallback if no equipment capacity UOM) */
  jobLoadUom?: Nullable<ItemNameAndId>;
  /** Job's main UOM (final fallback) */
  jobUom?: Nullable<ItemNameAndId>;
}
const LoadCard = ({
  companyId,
  cycleTime,
  index,
  isDeletable,
  isLast,
  load,
  onClick,
  onRemoveLoad,
  reload,
  sx,
  ticket,
  isEditing = false,
  readonly = false,
  jobState,
  updateLoad,
  disableApproval = false,
  isOrderEditable = false,
  equipmentCapacityUom,
  jobLoadUom,
  jobUom,
  workLockReason = null,
}: Props) => {
  const theme = useTheme();
  const { approveLoad, unapproveLoad } = useJob();
  const { userStore } = useStores();
  const userPermissions = userStore.getPermissions();

  const [isRemovingLoad, setIsRemovingLoad] = useState(false);

  // Check if this load's UOM mismatches the job's expected UOM
  // Use ticket UOM if available (what's displayed), otherwise fall back to load UOM
  const loadActualUom = ticket?.unitOfMeasure ?? load.unitOfMeasure;
  const hasUomMismatch = loadHasUomMismatch(
    equipmentCapacityUom,
    jobLoadUom,
    loadActualUom,
    jobUom,
  );
  const expectedUom = getExpectedJobUom(equipmentCapacityUom, jobLoadUom, jobUom);

  const hasCycleTime = cycleTime > 0;
  const ticketDate = ticket?.serviceDate || ticket?.ocrServiceDate || ticket?.createdAt;
  const formattedTicketTime = ticketDate
    ? dateFormat(new Date(ticketDate), 'h:mm A')
    : '-';
  const formattedTicketQuantity =
    ticket?.quantity && ticket?.unitOfMeasure?.name
      ? `${numberToIntegerOrDecimal(Number(ticket.quantity))} ${ticket.unitOfMeasure.name}`
      : '-';
  const ticketIsFlagged = ticket?.flagged || false;
  const loadLevelPricingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.loadLevelPricing,
  });
  // Only flag-gated divergent Loads (rate_inherited === false) get the chip; an
  // absent/null value (flag OFF, older payloads) renders exactly as before.
  const hasCustomRate = loadLevelPricingEnabled && load.rateInherited === false;
  const {
    isApproved: loadIsApproved,
    isApprovable: loadIsApprovable,
    isUnapprovable: loadIsUnapprovable,
  } = getApprovalStatus(load, userPermissions, isOrderEditable);

  const { dismiss: handleDismissAiReview, undismiss: handleUndoAiReviewDismissal } =
    useAiReviewDismissal({ companyId, load, onChanged: reload });

  const ApproveRowCTA = () => {
    if (loadIsApproved && loadIsUnapprovable) {
      return (
        <ApprovalActionsMenu
          onMenuItemClick={() => {
            unapproveLoad(load.id, companyId);
            reload();
          }}
          approved={loadIsApproved}
          isLoading={disableApproval}
        />
      );
    }

    if (loadIsApprovable) {
      return (
        <Button
          variant="outlined"
          color="secondary"
          onClick={async (e) => {
            e.stopPropagation();
            await approveLoad(load.id, companyId);
            trackLoadsApproved([load]);
            reload();
          }}
          sx={{ fontSize: '12px', minWidth: 0, px: '4px', py: 0, height: '24px' }}
          disabled={isEditing || disableApproval}
        >
          {t('approvals.approve')}
        </Button>
      );
    }

    return (
      <Chip
        color={loadIsApproved ? 'success' : 'secondary'}
        label={loadIsApproved ? t('status.approved') : t('status.pending')}
        hideDropdownArrow
      />
    );
  };

  const handleRemoveLoad = useCallback(
    async (event?: React.MouseEvent) => {
      event?.stopPropagation();

      try {
        setIsRemovingLoad(true);
        await onRemoveLoad(load.id);
      } finally {
        setIsRemovingLoad(false);
      }
    },
    [onRemoveLoad, load.id],
  );

  return (
    <Box
      aria-label={`load job ${load.id}`}
      onClick={() => {
        if (isRemovingLoad) {
          return;
        }

        onClick();
      }}
      sx={{
        borderBottom: isLast ? '0px' : `1px solid ${theme.brandV2.colors.treadGray6}`,
        cursor: readonly ? 'default' : 'pointer',
        ...(ticket?.isPotentialDuplicateFlagged && flaggedLoadRowSx(theme)),
        ...sx,
      }}
    >
      {/* Load number + Attachments and alerts */}
      <CardInfoContainer isColumn sx={{ alignSelf: 'center', alignItems: 'center' }}>
        <Typography variant="body2">{index + 1}</Typography>
      </CardInfoContainer>

      {/* Status and cycle */}
      <CardInfoContainer isColumn>
        <Box>
          {jobState ? (
            <LoadStatusTransitioner
              jobState={jobState}
              load={load}
              isDisabled={isEditing || readonly}
              onLoadUpdate={updateLoad}
            />
          ) : (
            <LoadStatus status={load.state} />
          )}
        </Box>

        <Typography variant="body2">
          {hasCycleTime ? minutesToHoursMins(cycleTime) : '-'}
        </Typography>
      </CardInfoContainer>

      {/* Ticket image */}
      <CardInfoContainer sx={{ mt: '4px', justifySelf: 'center' }}>
        {ticket?.previewUrl && (
          <Box
            sx={{
              display: 'flex',
              flex: 0,
              height: `${IMAGE_SIZE_IN_PX}px`,
              minWidth: `${IMAGE_SIZE_IN_PX}px`,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {ticket.source && <TicketSourceBadge ticketSource={ticket.source} />}
            <Box
              component="img"
              alt="ticket-preview"
              crossOrigin={ATTACHMENT_IMAGE_CROSS_ORIGIN}
              src={ticket.previewUrl}
              sx={{
                display: 'block',
                objectFit: 'cover',
                objectPosition: 'center',
                width: '100%',
              }}
            />
          </Box>
        )}
      </CardInfoContainer>

      {/* Ticket Qty / Add ticket CTA */}
      <CardInfoContainer isColumn sx={{ mt: '4px' }}>
        {ticket ? (
          <>
            <OverflowAwareText variant="body2">
              {ticket.ticketNumber || '-'}
            </OverflowAwareText>
            <DuplicateTicketIndicator ticket={ticket} />
            <TicketExportIndicator ticket={ticket} />
          </>
        ) : (
          <>
            {!readonly && (
              <Typography
                variant="body2"
                sx={{ color: theme.brandV2.colors.treadOrangeDark }}
              >
                <ControlPointOutlined sx={{ mr: 0.5, width: '16px', height: '16px;' }} />
                {t('loads.add_ticket')}
              </Typography>
            )}
          </>
        )}

        {workLockReason && (
          <Tooltip title={workLockReason}>
            <Typography
              variant="caption"
              data-ui="load-card__work-lock"
              sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}
            >
              <LockOutlined sx={{ width: '14px', height: '14px' }} />
              {t('work_lock.locked')}
            </Typography>
          </Tooltip>
        )}

        <OverflowAwareText variant="body2">{formattedTicketQuantity}</OverflowAwareText>
      </CardInfoContainer>

      {/* Ticket Time */}
      <CardInfoContainer isColumn sx={{ mt: '4px' }}>
        <Typography variant="body2">{formattedTicketTime}</Typography>
      </CardInfoContainer>

      {/* Pickup Entry */}
      <CardInfoContainer isColumn sx={{ mt: '4px' }}>
        {load.loadSummary?.arrivedPickupAt && (
          <>
            <Typography variant="body2">
              {dateFormat(load.loadSummary.arrivedPickupAt, 'hh:mm A')}
            </Typography>

            <Typography variant="body2" color={theme.brandV2.colors.treadGray2}>
              {load.loadSummary.pickupTimeMinutes
                ? formatDuration(load.loadSummary.pickupTimeMinutes * 60)
                : '0m'}
            </Typography>
          </>
        )}
      </CardInfoContainer>

      {/* Drop Off Entry */}
      <CardInfoContainer isColumn sx={{ mt: '4px' }}>
        {load.loadSummary?.arrivedDropoffAt ? (
          <>
            <Typography variant="body2">
              {dateFormat(load.loadSummary.arrivedDropoffAt, 'hh:mm A')}
            </Typography>

            <Typography variant="body2" color={theme.brandV2.colors.treadGray2}>
              {load.loadSummary.dropoffTimeMinutes
                ? formatDuration(load.loadSummary.dropoffTimeMinutes * 60)
                : '0m'}
            </Typography>
          </>
        ) : (
          load.loadSummary?.loadCompletedAt && (
            <Typography variant="body2">
              {dateFormat(load.loadSummary.loadCompletedAt, 'hh:mm A')}
            </Typography>
          )
        )}
      </CardInfoContainer>

      {/* Approve / Unapprove CTAs */}
      <CardInfoContainer sx={{ gap: 1 }}>
        <ApproveRowCTA />

        <LoadReviewIndicator
          aiReview={load.aiReview}
          canDismiss={loadIsApprovable || loadIsApproved}
          isTicketFlagged={ticketIsFlagged}
          onDismiss={handleDismissAiReview}
          onUndoDismiss={handleUndoAiReviewDismissal}
        />

        {hasUomMismatch && (
          <UomMismatchIndicator
            expectedUom={expectedUom?.name}
            actualUom={loadActualUom?.name}
          />
        )}

        {hasCustomRate && <LoadCustomRateIndicator load={load} />}
      </CardInfoContainer>

      {/* Remove row */}
      <CardInfoContainer sx={{ alignSelf: 'center' }}>
        {!readonly &&
          isDeletable &&
          (isRemovingLoad ? (
            <Box sx={{ width: '16px', height: '16px', ml: 'auto' }}>
              <LoadingSpinner isVisible loadingIndicatorSize={16} />
            </Box>
          ) : (
            <RemoveItemButton
              action={handleRemoveLoad}
              sx={{
                cursor: 'pointer',
                backgroundColor: 'transparent',
                width: '16px',
                height: '16px',
                ml: 'auto',
              }}
              icon={Clear}
              iconStyles={{
                color: theme.brandV2.colors.treadGray3,
                width: '16px',
                height: '16px',
              }}
              tooltipTitle={
                ticket ? t('loads.remove_load_and_ticket') : t('loads.remove_load')
              }
            />
          ))}
      </CardInfoContainer>

      {load.notes && (
        <Box
          sx={{
            gridColumn: '2 / -2',
            py: 1,
            px: 1,
            mb: 1,
            backgroundColor: theme.brandV2.colors.treadGray8,
            borderRadius: theme.spacing(0.5),
          }}
        >
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-line' }}
            color={theme.brandV2.colors.treadGray1}
          >
            {load.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

type CardInfoContainerProps = BoxOwnProps & {
  isColumn?: boolean;
};

const CardInfoContainer = styled(Box, {
  shouldForwardProp: (propName) => propName !== 'isColumn',
})<CardInfoContainerProps>(({ isColumn, theme }) => ({
  display: 'flex',
  flexDirection: isColumn ? 'column' : 'row',
  gap: '2px',
  overflow: 'hidden',
  padding: theme.spacing(1, 0),
}));

export default LoadCard;
